import { Close, ErrorSharp, Settings } from "@mui/icons-material";
import {
  Box,
  Drawer,
  Dropdown,
  IconButton,
  Link,
  Menu,
  MenuButton,
  MenuItem,
  Skeleton,
  Stack,
  Tooltip,
  Typography,
} from "@mui/joy";
import { UseQueryResult } from "@tanstack/react-query";
import { useNavigate } from "@tanstack/react-router";
import { format } from "date-fns-tz";
import { ApiCompetitionEntryDetail, ApiCompetitionEntryInline } from "griptonite-api-client";
import { useMemo } from "react";

import FlashedCircle from "@/assets/icons/FlashedCircle";
import TickedCircle from "@/assets/icons/TickedCircle";
import {
  useCompetitionEntryQuery,
  useCompetitionQuery,
  useCurrentCompetitionUuid,
} from "@/hooks/competitions";
import formatIFSCScoreTuple from "@/utils/formatIFSCScoreTuple";
import formatNumber, { getNumberWithOrdinal } from "@/utils/formatNumber";
import getCompetitionRouteDisplay from "@/utils/getCompetitionRouteDisplay";

import Avatar from "../Avatar";
import { useModal } from "../modals/useModal";
import { RouteCircle } from "../RouteCircle";

const InfoBox = ({ label, value }: { label: string; value: string }) => (
  <Stack alignItems="center">
    <Typography level="body-sm" lineHeight={1.2}>
      {label}
    </Typography>
    <Box
      sx={{
        backgroundColor: "neutral.100",
        paddingX: 0.5,
        paddingY: 0.5,
        borderRadius: 8,
        minWidth: 50,
        textAlign: "center",
        marginTop: 0.25,
      }}
    >
      <Typography level="body-sm" fontWeight="bold">
        {value}
      </Typography>
    </Box>
  </Stack>
);

const Header = ({ entry }: { entry?: ApiCompetitionEntryDetail | ApiCompetitionEntryInline }) => {
  const competitionUuid = useCurrentCompetitionUuid();
  const { data: competition } = useCompetitionQuery(competitionUuid);
  const category =
    typeof entry?.category === "object"
      ? entry.category
      : competition?.stages[0].categories.find((c) => c.uuid === entry?.category);

  const position = entry
    ? "categoryPosition" in entry
      ? entry.categoryPosition
      : entry.position
    : null;
  const positionOrdinal = position ? getNumberWithOrdinal(position) : null;

  const navigate = useNavigate();

  const { display: displayModal } = useModal();

  return (
    <Stack>
      <Stack direction="row" alignItems="center" sx={{ padding: 1 }} spacing={1}>
        <Avatar user={entry?.user} size={48} />
        <Stack sx={{ flex: 1 }}>
          <Typography level="title-md">
            {entry?.user.firstName} {entry?.user.lastName}
          </Typography>
          {entry?.user.email && !entry.user.email.match(/@signup.griptonite.io$/) ? (
            <Typography level="body-sm" fontSize="0.82rem" lineHeight={1} marginBottom={0.25}>
              <Link href={`mailto:${entry.user.email}`}>{entry.user.email}</Link>
            </Typography>
          ) : null}
          <Typography level="title-sm">{category?.name}</Typography>
        </Stack>
        <Dropdown>
          <MenuButton slots={{ root: IconButton }}>
            <Settings />
          </MenuButton>
          <Menu disablePortal placement="bottom-end">
            <MenuItem
              onClick={() =>
                competition &&
                entry &&
                displayModal("CHANGE_COMPETITOR_CATEGORY", {
                  competition,
                  entry,
                })
              }
            >
              Change category...
            </MenuItem>
            <MenuItem
              color="danger"
              onClick={() => {
                competition &&
                  entry &&
                  displayModal("CONFIRM_DELETE_COMPETITOR", {
                    competition,
                    entry,
                    onSuccess: () => {
                      navigate({
                        to: "..",
                        from: "/gym/$venueUuid/events/$competitionUuid/competitors/$userUuid",
                        replace: true,
                      });
                    },
                  });
              }}
            >
              Delete entry
            </MenuItem>
          </Menu>
        </Dropdown>
        <IconButton
          onClick={() =>
            navigate({
              to: "..",
              from: "/gym/$venueUuid/events/$competitionUuid/competitors/$userUuid",
              replace: true,
            })
          }
        >
          <Close />
        </IconButton>
      </Stack>
      {entry ? (
        <Stack
          sx={{
            padding: 1,
            paddingTop: 0,
            borderBottom: "1px solid",
            borderBottomColor: "neutral.100",
          }}
          direction="row"
          spacing={1.5}
          alignItems="center"
        >
          <InfoBox label="Position" value={positionOrdinal || "-"} />
          <InfoBox
            label="Score"
            value={
              competition?.scoringSystem === "IFSC_RANKING"
                ? formatIFSCScoreTuple(entry.scoreTuple)
                : `${formatNumber(entry.score, {
                    maxDecimals: competition?.scoringSystem === "IFSC_SPORT" ? 2 : 1,
                  })}pts`
            }
          />
          <InfoBox label="Tries" value={(entry.attemptsCount || 0).toString()} />
          {"successesCount" in entry ? (
            <InfoBox label="Tops" value={entry.successesCount.toString()} />
          ) : null}
        </Stack>
      ) : null}
    </Stack>
  );
};

const EntryRouteScores = ({
  entryQuery,
}: {
  entryQuery: UseQueryResult<ApiCompetitionEntryDetail | ApiCompetitionEntryInline>;
}) => {
  const attemptGroups =
    entryQuery.data && "stageScorecards" in entryQuery.data
      ? entryQuery.data.stageScorecards[0].venueGroups[0]?.attempts || []
      : [];
  const competitionUuid = useCurrentCompetitionUuid();
  const competition = useCompetitionQuery(competitionUuid).data;

  const categoryUuid =
    typeof entryQuery.data?.category === "string"
      ? entryQuery.data.category
      : entryQuery.data?.category?.uuid;

  const validCompetitionRouteGroupUuids = useMemo(() => {
    return competition?.routeGroups
      .filter((rg) => categoryUuid && rg.categories.includes(categoryUuid))
      .map((rg) => rg.uuid);
  }, [competition, categoryUuid]);

  const entry = entryQuery.data;

  if (!entry || !("stageScorecards" in entry) || !competition) {
    if (entryQuery.isFetching) {
      return (
        <Stack>
          {Array.from({ length: 10 }).map((_, i) => (
            <Stack
              key={i}
              direction="row"
              alignItems="center"
              spacing={1}
              sx={{
                padding: 1,
                borderBottom: "1px solid",
                borderBottomColor: "neutral.100",
              }}
            >
              <Skeleton variant="circular" width={24} height={24} />
              <Box sx={{ flex: 1 }}>
                <Skeleton variant="text" width={30} />
              </Box>
              <Stack alignItems="flex-end">
                <Skeleton variant="text" width={60} />
                <Skeleton variant="text" width={20} />
              </Stack>
            </Stack>
          ))}
        </Stack>
      );
    } else {
      return <Stack />;
    }
  }
  return (
    <Stack>
      {attemptGroups.map((attemptGroup) => {
        const isValidRouteGroup = attemptGroup.route.competitionRouteGroups.some((rg) =>
          validCompetitionRouteGroupUuids?.includes(rg.uuid),
        );
        return (
          <Stack
            key={attemptGroup.uuid}
            sx={{
              padding: 1,
              borderBottom: "1px solid",
              borderBottomColor: "neutral.100",
              ...(!isValidRouteGroup ? { backgroundColor: "danger.100" } : []),
            }}
            direction="row"
            alignItems="center"
          >
            {!isValidRouteGroup ? (
              <Box sx={{ color: "danger.800", marginRight: 1 }}>
                <Tooltip
                  size="md"
                  title={
                    <Box sx={{ padding: 0.5 }}>
                      <strong>Wrong category</strong>
                      <br />
                      The user ticked this route before it was moved to a different category to the
                      one they are competing in.
                      <br />
                      Please remove the attempts or contact support for help.
                    </Box>
                  }
                >
                  <ErrorSharp />
                </Tooltip>
              </Box>
            ) : null}
            <Stack sx={{ flex: 1 }}>
              <Box sx={{ display: "flex", flexDirection: "row", gap: 0.5 }}>
                <RouteCircle colors={(attemptGroup.route.colors || "#FFFFFF").split(":")} />
                <Typography level="body-sm" fontWeight="bold">
                  {
                    getCompetitionRouteDisplay(attemptGroup.route, {
                      routeGroups: competition?.routeGroups || [],
                      categoryUuid: entry.category.uuid,
                    }).number
                  }
                </Typography>
              </Box>
              <Typography level="body-sm" textColor="neutral.600" flex={1}>
                {format(attemptGroup.timestamp, "do MMM, HH:mm")}
              </Typography>
            </Stack>
            <Stack alignItems="flex-end">
              <Typography level="body-sm" fontWeight="bold">
                {competition.scoringSystem !== "IFSC_RANKING"
                  ? `${formatNumber(attemptGroup.score, {
                      maxDecimals: competition?.scoringSystem === "IFSC_SPORT" ? 2 : 1,
                    })}pts`
                  : null}
              </Typography>
              <Stack direction="row" alignItems="center" spacing={0.5}>
                {attemptGroup.success ? (
                  attemptGroup.isFirstAttempt &&
                  attemptGroup.count === 1 &&
                  competition.climbType !== "SPORT" ? (
                    <FlashedCircle size="sm" />
                  ) : (
                    <TickedCircle size="sm" />
                  )
                ) : null}
                <Typography level="body-sm">
                  {!!attemptGroup.segmentsCount && !attemptGroup.success
                    ? `Zone ${attemptGroup.segmentsCount}`
                    : null}
                  {!!attemptGroup.segmentsCount && !attemptGroup.success ? " | " : null}
                  {attemptGroup.count} {attemptGroup.count === 1 ? "try" : "tries"}
                </Typography>
              </Stack>
            </Stack>
          </Stack>
        );
      })}
    </Stack>
  );
};

export default function CompetitorSidebar({ userUuid }: { userUuid: string }) {
  const competitionUuid = useCurrentCompetitionUuid();
  const entryQuery = useCompetitionEntryQuery(competitionUuid, userUuid);
  return (
    <Drawer open anchor="right" sx={{ ".MuiDrawer-content": { maxWidth: 480 } }}>
      <Header entry={entryQuery.data} />
      <EntryRouteScores entryQuery={entryQuery} />
    </Drawer>
  );
}
